import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  T,
  Sum,
  Masks,
  useRespostaByPerguntaCode,
  C,
  averagePriceImpactosQualidade as avgPrice,
} from 'shared/helpers';
import PropTypes from 'prop-types';

import Styles from './Resumo.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import { Creators as reduxImpactos } from 'store/ducks/impactos';
import { Creators as reduxPerguntas } from 'store/ducks/perguntas';
import { Creators as reduxRespostas } from 'store/ducks/respostas';
import { Creators as reduxEtapas } from 'store/ducks/etapas';
import { Creators as reduxProdutos } from 'store/ducks/produtos';

import Header from 'components/Header/Header';
import Table from './Table/Table';

import { useChart1, useChart2, useChart3, useChart4 } from './charts';
import { useIntl } from 'react-intl';
import PrintButton from 'components/PrintButton/PrintButton';

function symbol(number) {
  return number < 0 ? '' : '+';
}

function DiffOverlay({
  values: [value1 = 0, value2 = 0],
  formatter,
  containerRef,
  tableRef,
}) {
  const diff = value1 - value2;
  let percent = (diff / value2) * 100 || 0;
  if (percent === Infinity) percent = 0;

  const { formatNumber } = useIntl();
  return value1 && value2 ? (
    <div className={Styles.Overlay} ref={containerRef}>
      <div ref={tableRef}>
        <div>
          <p>
            {symbol(percent)}
            {(formatter || formatNumber)(diff)}
          </p>
          <p>
            {symbol(percent)}
            {formatNumber(percent || 0, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            %
          </p>
        </div>
        <FontAwesomeIcon
          className={C(Styles.Icon, [diff >= 0, Styles.Positive])}
          icon={faArrowRight}
        />
      </div>
    </div>
  ) : null;
}

export default function Resumo({ match }) {
  const { Resumo, Charts, Chart } = Styles;
  const { id } = match.params;

  const dispatch = useDispatch();

  const Impactos = useSelector((state) => state.impactos.data);
  const Perguntas = useSelector((state) => state.perguntas.data || []);
  const Respostas = useSelector((state) => state.respostas.data || []);
  const REtapas = useSelector((state) => state.etapas.data);
  const PExpectativas = Perguntas.filter((x) => x.fase_id === 8);
  const Savings = Perguntas.filter((x) => x.fase_id === 7);

  useEffect(() => {
    dispatch(reduxImpactos.getImpactos(id));
    dispatch(
      reduxPerguntas.getPerguntas((x) => x.fase_id === 7 || x.fase_id === 8),
    );
    dispatch(reduxEtapas.getEtapas(id));
    dispatch(reduxProdutos.getProdutos());
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(
      reduxRespostas.getRespostas((x) => String(x.pem_id) === String(id)),
    );
  }, [id, dispatch, Perguntas]);

  const Etapas = REtapas.map((x) => ({
    ...x,
    produtosSQMV: x.produtos.filter((y) => y.fase_id === 2),
    produtosCOND: x.produtos.filter((y) => y.fase_id === 3),
  }));

  const Totals = Etapas.map((etapa) => {
    let totalSQMV = 0;
    let totalCOND = 0;
    for (let i = 0; i < etapa.produtosSQMV.length; i++) {
      totalSQMV += Number(
        (etapa.produtosSQMV[i].valor / 1000) * etapa.produtosSQMV[i].quantidade,
      );
    }
    for (let i = 0; i < etapa.produtosCOND.length; i++) {
      totalCOND += Number(
        (etapa.produtosCOND[i].valor / 1000) * etapa.produtosCOND[i].quantidade,
      );
    }

    return [totalSQMV, totalCOND];
  });
  const areaTotal = useRespostaByPerguntaCode(PExpectativas, Respostas, 'areaTotal');

  const Resposta = (codPergunta) =>
    T(
      T(Respostas, []).find(
        (x) =>
          String(x.pergunta_id) ===
          String(
            T(T(PExpectativas, []).find((p) => p.code === codPergunta)).id,
          ),
      ),
    ).resposta;
  const totalSavings = Sum(
    Respostas.filter((x) =>
      Savings.map((y) => y.id).includes(x.pergunta_id),
    ).map((z) => T(z).resposta || 0),
  );

  const produtividadeConvencional = useRespostaByPerguntaCode(
    Perguntas,
    Respostas,
    'produtividadeHa',
  );

  const finalCosts = [
    Sum(Totals.map((x) => x[0])) - totalSavings,
    Sum(Totals.map((x) => x[1])),
  ];

  const productivity = [
    T(Impactos).produtividade || 0,
    produtividadeConvencional,
  ];
  const avgPrices = [
    avgPrice(T(Impactos), 'svb', produtividadeConvencional) /
      T(Impactos).produtividade || 0,
    avgPrice(T(Impactos), 'convencional', produtividadeConvencional) /
      produtividadeConvencional,
  ];
  const fullIncome = [
    avgPrices[0] * T(Impactos).produtividade || 0,
    avgPrices[1] * produtividadeConvencional || 0,
  ];
  const finalIncome = [
    fullIncome[0] - finalCosts[0] || 0,
    fullIncome[1] - finalCosts[1] || 0,
  ];
  const data = {
    finalCosts,
    productivity,
    avgPrices,
    fullIncome,
    finalIncome,
  };

  const { anychart } = window;

  const UNIT = useRespostaByPerguntaCode(
    PExpectativas,
    Respostas,
    'unidadeProducao',
  );
  const container1 = useRef();
  const table1 = useRef();
  const chartRefs = [useRef(), useRef(), useRef(), useRef()];
  const [chart1Ref, chart2Ref, chart3Ref, chart4Ref] = chartRefs;
  const chart1 = useChart1(
    chart1Ref,
    anychart,
    T(Impactos),
    Resposta,
    container1,
    table1,
  );
  const container2 = useRef();
  const table2 = useRef();
  const chart2 = useChart2(
    chart2Ref,
    anychart,
    data.fullIncome[0],
    data.fullIncome[1],
    container2,
    table2,
  );
  const container3 = useRef();
  const table3 = useRef();
  const chart3 = useChart3(
    chart3Ref,
    anychart,
    data.finalIncome[0],
    data.finalIncome[1],
    container3,
    table3,
  );
  useChart4(
    chart4Ref,
    anychart,
    data.fullIncome[0],
    data.finalIncome[0],
    data.fullIncome[1],
    data.finalIncome[1],
  );

  const tableRef = useRef();

  if (!anychart) {
    return null;
  }

  return (
    <div className={Resumo}>
      <Header title={'RESUMO'} icon={faChartLine}>
        <PrintButton fitElements={chartRefs} />
      </Header>
      <Table
        data={data}
        totalArea={areaTotal}
        productionUnit={UNIT}
        tableRef={tableRef}
      />
      <div id='charts' className={Charts}>
        <div className={Chart}>
          <div id='chart1' />
          <DiffOverlay
            values={chart1}
            containerRef={container1}
            tableRef={table1}
            formatter={(value = 0) => Masks.number(value, { suffix: ' /ha' })}
          />
        </div>
        <div className={Chart}>
          <div id='chart2' />
          <DiffOverlay
            values={chart2}
            containerRef={container2}
            tableRef={table2}
            formatter={(value = 0) => Masks.currency(value, { suffix: ' /ha' })}
          />
        </div>
        <div className={Chart}>
          <div id='chart3' />
          <DiffOverlay
            values={chart3}
            containerRef={container3}
            tableRef={table3}
            formatter={(value = 0) => Masks.currency(value, { suffix: ' /ha' })}
          />
        </div>
        <div className={Chart}>
          <div id='chart4' />
        </div>
      </div>
    </div>
  );
}

Resumo.propTypes = {
  match: PropTypes.any.isRequired,
};
